import { useUploadGuard } from '../hooks/useUploadGuard';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DownloadIcon from '@mui/icons-material/Download';
import { appendWorkflowName, StageBackButton, useFlow } from '../components/StageWrapper';
import {
  fillComplexServicesMrr,
  getComplexServicesMrr,
  saveComplexServicesMrr,
} from '../api/complexServicesFunctions';
import { toUserFacingError } from '../utils/userFacingError';
import { BadgeSlot } from './complexServicesFields';
import SimpleStageTour, { type SimpleStageTourHandle } from '../components/tour/SimpleStageTour';
import { steps } from './ComplexServicesMrr.tour';
import { SavedWorkActionRow } from '../components/SavedWorkActionRow';
import { StageTourButton } from '../components/StageTourButton';

/** Complex Services — Market Research Report (stage 618).
 *
 * Vendor Discovery is useful input to this report, but it is not proof that a
 * reviewed MRR exists. This form keeps that provenance visible while requiring
 * the report metadata, analysis, and a server-verified uploaded PDF/DOCX before
 * it will release the user to the next package step.
 */
export interface ComplexServicesStageProps {
  flowId?: string | null;
  initialData?: unknown;
  onComplete: (data?: unknown) => void;
}

interface Evidence {
  source: string;
  summary: string;
  provenance?: string;
  source_stage_code?: number;
}

interface ArtifactRef {
  artifact_id?: string;
  stage_code?: number;
  filename?: string;
  source?: string;
  [key: string]: unknown;
}

interface MrrData {
  report_title: string;
  report_date: string;
  prepared_by: string;
  requirement_summary: string;
  market_research_summary: string;
  source_evidence: Evidence[];
  supplier_research_findings: string;
  conclusion_recommendation: string;
  artifact_ref?: ArtifactRef;
  artifact?: ArtifactRef;
  artifact_satisfied?: boolean;
  is_complete?: boolean;
  meta_auto_filled_fields?: string[];
  meta_user_edited_fields?: string[];
}

const EMPTY: MrrData = {
  report_title: '',
  report_date: '',
  prepared_by: '',
  requirement_summary: '',
  market_research_summary: '',
  source_evidence: [{ source: '', summary: '' }],
  supplier_research_findings: '',
  conclusion_recommendation: '',
};

const ACCEPTED = '.pdf,.docx';
const DOCX_CONTENT_TYPE =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
}

/** Handle both normal client output and a defensive `{ data: output }` envelope. */
function unwrap(value: unknown): Record<string, unknown> {
  const record = asRecord(value);
  return asRecord(record.data ?? value);
}

function evidenceFrom(value: unknown): Evidence[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((item) => item && typeof item === 'object')
    .map((item) => {
      const e = item as Record<string, unknown>;
      return {
        source: String(e.source ?? ''),
        summary: String(e.summary ?? ''),
        provenance: typeof e.provenance === 'string' ? e.provenance : undefined,
        source_stage_code: typeof e.source_stage_code === 'number' ? e.source_stage_code : undefined,
      };
    });
}

function fromData(value: unknown): MrrData {
  const d = unwrap(value);
  const artifact = asRecord(d.artifact_ref ?? d.artifact);
  return {
    ...EMPTY,
    report_title: String(d.report_title ?? ''),
    report_date: String(d.report_date ?? ''),
    prepared_by: String(d.prepared_by ?? ''),
    requirement_summary: String(d.requirement_summary ?? ''),
    market_research_summary: String(d.market_research_summary ?? ''),
    source_evidence: evidenceFrom(d.source_evidence).length
      ? evidenceFrom(d.source_evidence)
      : [{ source: '', summary: '' }],
    supplier_research_findings: String(d.supplier_research_findings ?? ''),
    conclusion_recommendation: String(d.conclusion_recommendation ?? ''),
    artifact_ref: Object.keys(artifact).length ? (artifact as ArtifactRef) : undefined,
    artifact_satisfied: d.artifact_satisfied === true,
    is_complete: d.is_complete === true,
    meta_auto_filled_fields: Array.isArray(d.meta_auto_filled_fields)
      ? d.meta_auto_filled_fields.filter((field): field is string => typeof field === 'string')
      : [],
    meta_user_edited_fields: Array.isArray(d.meta_user_edited_fields)
      ? d.meta_user_edited_fields.filter((field): field is string => typeof field === 'string')
      : [],
  };
}

function isAllowedReport(file: File): boolean {
  const ext = file.name.split('.').pop()?.toLowerCase();
  return ext === 'pdf' || ext === 'docx';
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read the report file.'));
    reader.onload = () => {
      const bytes = new Uint8Array(reader.result as ArrayBuffer);
      let binary = '';
      const chunkSize = 0x8000;
      for (let start = 0; start < bytes.length; start += chunkSize) {
        binary += String.fromCharCode(...Array.from(bytes.subarray(start, start + chunkSize)));
      }
      resolve(btoa(binary));
    };
    reader.readAsArrayBuffer(file);
  });
}

function hasTrustedArtifact(data: MrrData): boolean {
  // This deliberately accepts only the backend's completion projection. A local
  // filename/base64 payload or Vendor Discovery prefill can never forge this.
  return Boolean(
    data.artifact_satisfied === true &&
      data.artifact_ref?.artifact_id &&
      data.artifact_ref.stage_code === 618 &&
      data.artifact_ref.source === 'uploaded',
  );
}

function fieldFilled(value: string): boolean {
  return Boolean(value.trim());
}

function evidenceComplete(evidence: Evidence[]): boolean {
  return evidence.length > 0 && evidence.every((item) => fieldFilled(item.source) && fieldFilled(item.summary));
}

const ComplexServicesMrr: React.FC<ComplexServicesStageProps> = ({ flowId, initialData, onComplete }) => {
  const tourRef = useRef<SimpleStageTourHandle>(null);
  const { flowName, isViewOnly, isSampleFlow, onSampleContinue } = useFlow();
  const { disabledRef: readOnlyRef, openPicker, blockEvent } = useUploadGuard(isViewOnly);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [form, setForm] = useState<MrrData>(() => fromData(initialData));
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isFilling, setIsFilling] = useState(false);
  const [useSavedWork, setUseSavedWork] = useState(true);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [autoFilled, setAutoFilled] = useState<Set<string>>(
    () => new Set(fromData(initialData).meta_auto_filled_fields),
  );
  const [userEdited, setUserEdited] = useState<Set<string>>(
    () => new Set(fromData(initialData).meta_user_edited_fields),
  );
  const autoFilledRef = useRef(autoFilled);
  const userEditedRef = useRef(userEdited);
  const lastSavedRef = useRef<string | null>(null);
  const hasLocalEditsRef = useRef(false);

  const requiredDataComplete = useMemo(
    () =>
      fieldFilled(form.report_title) &&
      fieldFilled(form.report_date) &&
      fieldFilled(form.prepared_by) &&
      fieldFilled(form.requirement_summary) &&
      fieldFilled(form.market_research_summary) &&
      evidenceComplete(form.source_evidence) &&
      fieldFilled(form.supplier_research_findings) &&
      fieldFilled(form.conclusion_recommendation),
    [form],
  );
  const trustedArtifact = hasTrustedArtifact(form);
  const canContinue = !isViewOnly && !saving && !isFilling && requiredDataComplete && trustedArtifact;

  const buildPayload = useCallback(
    (filePayload?: Record<string, string>) => ({
      report_title: form.report_title,
      report_date: form.report_date,
      prepared_by: form.prepared_by,
      requirement_summary: form.requirement_summary,
      market_research_summary: form.market_research_summary,
      source_evidence: form.source_evidence,
      supplier_research_findings: form.supplier_research_findings,
      conclusion_recommendation: form.conclusion_recommendation,
      meta_auto_filled_fields: Array.from(autoFilled),
      meta_user_edited_fields: Array.from(userEdited),
      ...(filePayload ?? {}),
    }),
    [autoFilled, form, userEdited],
  );

  // Fill responses may contain a newer generated draft. Preserve every locally
  // edited field while accepting the rest of that draft, then retain both sides
  // of the provenance contract for the next autosave.
  const applyResponse = useCallback((response: unknown) => {
    const restored = fromData(response);
    const protectedFields = new Set([
      ...Array.from(userEditedRef.current),
      ...(restored.meta_user_edited_fields ?? []),
    ]);
    setForm((current) => {
      const next = { ...restored };
      (Object.keys(EMPTY) as Array<keyof MrrData>).forEach((field) => {
        if (protectedFields.has(field)) {
          (next as Record<string, unknown>)[field] = current[field];
        }
      });
      return next;
    });
    userEditedRef.current = protectedFields;
    setUserEdited(protectedFields);
    const nextAutoFilled = new Set(
      [...Array.from(autoFilledRef.current), ...(restored.meta_auto_filled_fields ?? [])]
        .filter((field) => !protectedFields.has(field)),
    );
    autoFilledRef.current = nextAutoFilled;
    setAutoFilled(nextAutoFilled);
    return restored;
  }, []);

  // Hydrate the durable report + the backend-produced Vendor Discovery prefill.
  useEffect(() => {
    let cancelled = false;
    if (!flowId) return undefined;
    (async () => {
      try {
        const data = await getComplexServicesMrr(flowId);
        if (cancelled || !data || hasLocalEditsRef.current) return;
        const restored = applyResponse(data);
        lastSavedRef.current = JSON.stringify({
          report_title: restored.report_title,
          report_date: restored.report_date,
          prepared_by: restored.prepared_by,
          requirement_summary: restored.requirement_summary,
          market_research_summary: restored.market_research_summary,
          source_evidence: restored.source_evidence,
          supplier_research_findings: restored.supplier_research_findings,
          conclusion_recommendation: restored.conclusion_recommendation,
          meta_auto_filled_fields: restored.meta_auto_filled_fields,
          meta_user_edited_fields: restored.meta_user_edited_fields,
        });
      } catch {
        // Hydration is best effort; an empty draft remains safely usable.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applyResponse, flowId]);

  useEffect(() => {
    if (initialData) lastSavedRef.current = JSON.stringify(buildPayload());
    // The initial-data baseline must be set once, before any blur.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const autoSave = useCallback(() => {
    if (!flowId || isViewOnly || saving) return;
    const payload = buildPayload();
    const snapshot = JSON.stringify(payload);
    if (snapshot === lastSavedRef.current) return;
    lastSavedRef.current = snapshot;
    void saveComplexServicesMrr(flowId, payload)
      .then((data) => {
        if (data) applyResponse(data);
      })
      .catch(() => {
        // A later blur should retry a failed draft save.
        lastSavedRef.current = null;
      });
  }, [applyResponse, buildPayload, flowId, isViewOnly, saving]);

  const markUserEdited = (field: string) => {
    hasLocalEditsRef.current = true;
    if (userEditedRef.current.has(field)) return;
    const next = new Set(userEditedRef.current).add(field);
    userEditedRef.current = next;
    setUserEdited(next);
  };

  const update = (field: keyof Pick<MrrData,
    'report_title' | 'report_date' | 'prepared_by' | 'requirement_summary' |
    'market_research_summary' | 'supplier_research_findings' | 'conclusion_recommendation'>,
    value: string) => {
    markUserEdited(field);
    setForm((current) => ({ ...current, [field]: value }));
  };

  const updateEvidence = (index: number, field: 'source' | 'summary', value: string) => {
    markUserEdited('source_evidence');
    setForm((current) => ({
      ...current,
      source_evidence: current.source_evidence.map((item, idx) =>
        idx === index ? { ...item, [field]: value, provenance: item.provenance === 'prefill' ? 'user_edited' : item.provenance } : item,
      ),
    }));
  };

  const addEvidence = () => {
    markUserEdited('source_evidence');
    setForm((current) => ({
      ...current,
      source_evidence: [...current.source_evidence, { source: '', summary: '' }],
    }));
  };

  const handleFill = async () => {
    if (!flowId || isViewOnly) return;
    setIsFilling(true);
    setError(null);
    try {
      const data = await (fillComplexServicesMrr as unknown as (
        id: string,
        savedWork: boolean,
      ) => Promise<unknown>)(flowId, useSavedWork);
      if (data) applyResponse(data);
    } catch (caught) {
      setError(toUserFacingError(caught));
    } finally {
      setIsFilling(false);
    }
  };

  const badgeFor = (field: string) => {
    if (userEdited.has(field)) return 'User Edited';
    if (autoFilled.has(field)) return 'Auto-filled';
    return null;
  };

  const acceptFile = async (file: File) => {
    if (readOnlyRef.current) return;
    if (!isAllowedReport(file)) {
      setUploadError('Invalid file format. Please upload a PDF or DOCX report.');
      return;
    }
    if (!flowId) {
      setUploadError('Save this workflow before uploading a report artifact.');
      return;
    }
    setUploadError(null);
    setError(null);
    setPendingFile(file);
    setSaving(true);
    try {
      const report_file_b64 = await fileToBase64(file);
      if (readOnlyRef.current) return;
      const response = await saveComplexServicesMrr(flowId, buildPayload({
        report_file_b64,
        report_filename: file.name,
        report_content_type: file.type || (file.name.toLowerCase().endsWith('.docx') ? DOCX_CONTENT_TYPE : 'application/pdf'),
      }));
      const restored = applyResponse(response);
      // A generic draft endpoint may acknowledge the structured save while the
      // upload route is being wired. Make that state explicit rather than
      // treating the local selection as a verified artifact.
      if (!hasTrustedArtifact(restored)) {
        setUploadError('The report was saved, but its artifact has not yet been verified. Please retry after upload processing completes.');
      }
    } catch (caught) {
      setError(toUserFacingError(caught));
    } finally {
      setSaving(false);
    }
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(false);
    const file = event.dataTransfer.files?.[0];
    if (file) void acceptFile(file);
  };

  const handleContinue = () => {
    if (!canContinue) return;
    onComplete(form);
  };

  const textField = (
    field: keyof Pick<MrrData,
      'report_title' | 'report_date' | 'prepared_by' | 'requirement_summary' |
      'market_research_summary' | 'supplier_research_findings' | 'conclusion_recommendation'>,
    label: string,
    testId: string,
    options: { type?: string; multiline?: boolean; minRows?: number } = {},
  ) => {
    const value = form[field] as string;
    const missing = !fieldFilled(value);
    const badge = badgeFor(field);
    return (
      <Box data-testid={`complex-services-mrr-${testId}-required`}>
        <BadgeSlot badge={badge === 'Auto-filled' ? 'auto-filled' : badge === 'User Edited' ? 'user-edited' : null} />
        <TextField
          label={label}
          value={value}
          onChange={(event) => update(field, event.target.value)}
          required
          fullWidth
          size="small"
          type={options.type}
          multiline={options.multiline}
          minRows={options.minRows}
          error={!isViewOnly && missing}
          helperText={!isViewOnly && missing ? 'Required' : undefined}
          InputLabelProps={options.type === 'date' ? { shrink: true } : undefined}
          InputProps={isViewOnly ? { readOnly: true } : undefined}
        />
      </Box>
    );
  };

  const artifactName = form.artifact_ref?.filename;
  return (
    <Box onBlur={autoSave}>
      <SimpleStageTour ref={tourRef} tourKey="complex-services-618" steps={steps} ready={!isFilling} />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 0.5, mb: 0.5 }}>
        <Typography variant="h4" data-testid="complex-services-mrr-title" sx={{ flex: '1 1 12rem', minWidth: 0, overflowWrap: 'anywhere' }}>
          {appendWorkflowName('Market Research Report', flowName)}
        </Typography>
        <StageTourButton onClick={() => tourRef.current?.replay()} />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Turn vendor discovery and other market-research inputs into the reviewed MRR package
        artifact. Vendor Discovery is prefill evidence only; it is not the report itself.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0, mb: 0.5, '& .MuiFormControlLabel-root': { m: 0, minHeight: 44 } }}>
        <SavedWorkActionRow
          action={
            <Button
              variant="contained"
              data-testid="complex-services-mrr-fill"
              onClick={isViewOnly ? undefined : handleFill}
              disabled={isViewOnly || isFilling}
              startIcon={isFilling ? <CircularProgress size={18} /> : undefined}
            >
              {isFilling ? 'Filling…' : 'Fill in MRR'}
            </Button>
          }
          savedWork={
            <FormControlLabel
              control={<Checkbox data-testid="complex-services-mrr-use-saved-work" checked={useSavedWork} onChange={isViewOnly ? undefined : (event) => setUseSavedWork(event.target.checked)} inputProps={{ readOnly: isViewOnly }} />}
              label="Use Saved Work"
            />
          }
        />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {uploadError && <Alert severity="warning" sx={{ mb: 2 }} data-testid="complex-services-mrr-upload-error">{uploadError}</Alert>}
      {!isSampleFlow && !canContinue && !isViewOnly && (
        <Alert severity="info" sx={{ mb: 2 }} data-testid="complex-services-mrr-required-validation">
          Complete the required report fields and upload a verified report artifact before continuing.
        </Alert>
      )}

      {isFilling ? (
        <Box data-testid="complex-services-mrr-filling" sx={{ minHeight: 240, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">Filling in the MRR from saved workflow data…</Typography>
        </Box>
      ) : (
      <Box data-testid="complex-services-mrr-form">
      <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }} data-testid="complex-services-mrr-report-review">
        <Typography variant="subtitle1" gutterBottom>Report review</Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={12} md={6}>{textField('report_title', 'Report title', 'report-title')}</Grid>
          <Grid item xs={12} md={3}>{textField('report_date', 'Report date', 'report-date', { type: 'date' })}</Grid>
          <Grid item xs={12} md={3}>{textField('prepared_by', 'Prepared by', 'prepared-by')}</Grid>
          <Grid item xs={12}>{textField('requirement_summary', 'Requirement summary', 'requirement-summary', { multiline: true, minRows: 3 })}</Grid>
          <Grid item xs={12}>{textField('market_research_summary', 'Market research summary', 'market-research-summary', { multiline: true, minRows: 3 })}</Grid>
          <Grid item xs={12}>{textField('supplier_research_findings', 'Supplier/research findings', 'supplier-research-findings', { multiline: true, minRows: 3 })}</Grid>
          <Grid item xs={12}>{textField('conclusion_recommendation', 'Conclusion/recommendation', 'conclusion-recommendation', { multiline: true, minRows: 3 })}</Grid>
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }} data-testid="complex-services-mrr-sources">
        <Typography variant="subtitle1" gutterBottom>Sources and evidence</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Record the sources used to support this report. Evidence prefilled from Vendor Discovery
          remains labelled as input so it cannot be mistaken for a completed MRR.
        </Typography>
        {form.source_evidence.map((item, index) => {
          const missing = !fieldFilled(item.source) || !fieldFilled(item.summary);
          const prefill = item.provenance === 'prefill';
          return (
            <Box key={index} data-testid={`complex-services-mrr-evidence-${index}`} sx={{ mb: 1 }}>
              <Typography variant="caption" color={prefill ? 'primary' : 'text.secondary'}>
                {prefill ? `Vendor Discovery (stage ${item.source_stage_code ?? 606}) — Prefill evidence; review and supplement it for this MRR.` : 'Report evidence'}
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={4}>
                  <BadgeSlot badge={badgeFor('source_evidence') === 'Auto-filled' ? 'auto-filled' : badgeFor('source_evidence') === 'User Edited' ? 'user-edited' : null} />
                  <TextField label="Source" value={item.source} required fullWidth size="small"
                    error={!isViewOnly && !fieldFilled(item.source)} helperText={!isViewOnly && !fieldFilled(item.source) ? 'Required' : undefined}
                    onChange={(event) => updateEvidence(index, 'source', event.target.value)} InputProps={isViewOnly ? { readOnly: true } : undefined} />
                </Grid>
                <Grid item xs={12} md={8}>
                  <BadgeSlot badge={badgeFor('source_evidence') === 'Auto-filled' ? 'auto-filled' : badgeFor('source_evidence') === 'User Edited' ? 'user-edited' : null} />
                  <TextField label="Evidence summary" value={item.summary} required fullWidth size="small"
                    error={!isViewOnly && !fieldFilled(item.summary)} helperText={!isViewOnly && !fieldFilled(item.summary) ? 'Required' : undefined}
                    onChange={(event) => updateEvidence(index, 'summary', event.target.value)} InputProps={isViewOnly ? { readOnly: true } : undefined} />
                </Grid>
              </Grid>
              {!isViewOnly && missing && <Typography variant="caption" color="error">Complete this evidence entry.</Typography>}
            </Box>
          );
        })}
        {!isViewOnly && (
          <Button size="small" onClick={addEvidence}>
            Add evidence source
          </Button>
        )}
        {form.source_evidence.length === 0 && !isViewOnly && <Typography color="error" variant="caption">At least one evidence source is required.</Typography>}
      </Paper>

      <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
        <Typography variant="subtitle1" gutterBottom>Reviewed MRR artifact</Typography>
        <input ref={fileInputRef} type="file" onClick={(event) => event.stopPropagation()} disabled={isViewOnly} accept={ACCEPTED} style={{ display: 'none' }}
          onChange={(event) => { const file = event.target.files?.[0]; if (file) void acceptFile(file); }} />
        <Paper variant="outlined" data-testid="complex-services-mrr-dropzone"
          onClick={() => !isViewOnly && openPicker(fileInputRef.current)}
          onDrop={handleDrop} onDragEnterCapture={blockEvent} onDragOverCapture={blockEvent} onDropCapture={blockEvent} onDragOver={(event) => { event.preventDefault(); if (!readOnlyRef.current) setDragOver(true); }} onDragLeave={() => setDragOver(false)}
          sx={{ p: 1.5, cursor: isViewOnly ? 'default' : 'pointer', border: '2px dashed', borderColor: dragOver ? 'primary.main' : trustedArtifact ? 'success.main' : 'grey.300', backgroundColor: dragOver ? 'action.hover' : trustedArtifact ? 'success.light' : 'background.paper' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Typography variant={pendingFile || trustedArtifact ? 'body2' : 'caption'} sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
              {pendingFile ? pendingFile.name : trustedArtifact ? artifactName : 'Drop reviewed MRR here or click to browse · .pdf, .docx'}
            </Typography>
            {saving ? <CircularProgress size={24} sx={{ flexShrink: 0 }} /> : trustedArtifact ? <CheckCircleOutlineIcon color="success" sx={{ fontSize: 26, flexShrink: 0 }} /> : <CloudUploadIcon sx={{ fontSize: 26, flexShrink: 0, color: 'text.secondary' }} />}
          </Box>
        </Paper>
        <Box sx={{ mt: 2 }} data-testid="complex-services-mrr-artifact-status">
          {trustedArtifact ? (
            <Typography color="success.main">Verified artifact: {artifactName}</Typography>
          ) : (
            <Typography color="text.secondary">Report artifact required — Vendor Discovery evidence does not complete this stage.</Typography>
          )}
          {trustedArtifact && flowId && <Button component="a" href={`${process.env.REACT_APP_API_URL || ''}/api/complex-services/${encodeURIComponent(flowId)}/mrr/artifact`} size="small" startIcon={<DownloadIcon />} data-testid="complex-services-mrr-download">Download verified report</Button>}
        </Box>
      </Paper>
      </Box>
      )}

      <Divider sx={{ mb: 2 }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
        <StageBackButton />
        <Button variant="contained" onClick={isSampleFlow ? onSampleContinue : handleContinue} disabled={!isSampleFlow && (isViewOnly || !canContinue)} data-testid="complex-services-mrr-continue">
          Continue
        </Button>
      </Box>
    </Box>
  );
};

export default ComplexServicesMrr;
